import useUrlState from "@ahooksjs/use-url-state";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { dateValues } from "components/dataGridFilter/FiltersData";
import { FilterChanges } from "components/dataGridFilter/GridFilters";
import { CursorPagination } from "pages/data/DataPage";
import { removeEmptyValuesFromObject } from "utils/redirectDataPage";

interface UseDataFilterStateAtt {
  pageSize: number;
  searchTerm?: string;
  serverFilterBaseUrl: string;
  cursorImageQueryId: CursorPagination;
}

export interface DataPageQueryParams {
  detector_id: string;
  filterModel: string;
}

export const useDataFilterState = ({
  pageSize,
  searchTerm = "",
  cursorImageQueryId,
  serverFilterBaseUrl,
}: UseDataFilterStateAtt) => {
  const { state: initialState }: Record<string, any> = useLocation();
  const defaultFilterState: Record<string, any> = {
    // this stores the state of the page in the url
    from: "",
    limit: "",
    search: "",
    ordering: "",
    detector_id: "",
    filterModel: JSON.stringify({
      items: [],
    }),
    grid_view: "false",
  };

  // this stores the state of the page in the url
  const [filterStateUrl, setFilterState] = useUrlState(initialState || defaultFilterState);

  const filterState: Record<string, any> = useMemo(
    () => ({
      search: searchTerm,
      last_n: filterStateUrl.limit,
      ordering: filterStateUrl.ordering,
      last_range_date: filterStateUrl.from,
      detector_id: filterStateUrl.detector_id,
      filterModel: JSON.parse(filterStateUrl.filterModel),
      grid_view: filterStateUrl.grid_view === "true",
    }),
    [filterStateUrl, searchTerm]
  );

  const filtersObject = useMemo(() => {
    const toString = (arr: string[]) => arr.toString().replaceAll(",", ";");
    const filterColumns = filterState.filterModel.items.map((i: any) => {
      if (i.columnField === "date") {
        if (!dateValues.includes(i.value)) {
          return "custom_date";
        }
        return "date";
      }
      return i.columnField;
    });
    const filterValues = filterState.filterModel.items.map((i: any) => {
      if (i.columnField === "date" && !dateValues.includes(i.value)) {
        const [startDate, endDate] = i.value.split("_");
        const utcStartDate = dayjs(startDate, "YYYY-MM-DD HH:mm:ss").utc().format("YYYY-MM-DD HH:mm:ss");
        const utcEndDate = dayjs(endDate, "YYYY-MM-DD HH:mm:ss").utc().format("YYYY-MM-DD HH:mm:ss");
        return `${utcStartDate}_${utcEndDate}`;
      }
      return i.value;
    });
    const filterOperators = filterState.filterModel.items.map((i: any) => {
      if (i.operatorValue === "true" || i.operatorValue === "false") {
        return "empty";
      }

      return i.operatorValue || "=";
    });

    return {
      search: searchTerm,
      last_n: filterState.last_n,
      page_size: pageSize.toString(),
      ordering: filterState.ordering,
      detector_id: filterState.detector_id,
      filter_values: JSON.stringify(filterValues),
      filter_columns: toString(filterColumns),
      paginate_from: cursorImageQueryId.cursor,
      filter_operators: toString(filterOperators),
      last_range_date: filterState.last_range_date,
    };
  }, [cursorImageQueryId.cursor, filterState, pageSize, searchTerm]);

  const parameterizedUrl = useMemo(() => {
    const object: Record<string, string> = {
      ...filtersObject,
    };
    const queryString = new URLSearchParams(removeEmptyValuesFromObject(object)).toString();

    return `${serverFilterBaseUrl}${queryString}`;
  }, [serverFilterBaseUrl, filtersObject]);

  const onFilterChange = (objToUpdate: Record<string, string>) => {
    setFilterState((prev) => ({
      ...prev,
      ...objToUpdate,
    }));
  };

  const onFilterModelChange = (objToUpdate: FilterChanges[]) => {
    const data = {
      filterModel: JSON.stringify({
        items: objToUpdate.map((item) => ({
          value: item.value,
          columnField: item.columnField,
          operatorValue: item.operatorValue,
        })),
      }),
    };
    setFilterState((prev) => ({
      ...prev,
      ...data,
    }));
  };

  return { parameterizedUrl, filterState, onFilterChange, onFilterModelChange, filtersObject };
};
